import {
  triggerActionLocoKeys,
  triggerDescriptionLocoKeys,
} from 'modules/automation-rules/triggers/constants/trigger-loco-keys'
import {
  AutomationRuleTriggersDataEnum,
  AutomationRuleTriggersEnum,
} from 'modules/automation-rules/triggers/enums/trigger-enum'
import {
  TriggersDataInterface,
  useTriggersData,
} from 'modules/automation-rules/triggers/hooks/use-triggers-data'
import { AutomationRulesTriggersEditType } from 'modules/automation-rules/triggers/types/automation-rule-triggers-edit-interface'
import { getTriggerIcon } from 'modules/automation-rules/triggers/utils/get-trigger-icon'
import React from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { CloseIcon } from '@systemeio/ui/icons'

export interface WrapperChildrenPropsInterface {
  renderTriggerLabel: ({
    label,
    additionalLabel,
  }: {
    label: string
    additionalLabel?: JSX.Element
  }) => JSX.Element
  onOpen: (slug: AutomationRuleTriggersDataEnum) => void
  onClear: (slug: AutomationRuleTriggersDataEnum) => void
  data: TriggersDataInterface
}
type ChildrenType = (args: WrapperChildrenPropsInterface) => JSX.Element

interface RuleTriggerWrapperProps {
  trigger: AutomationRulesTriggersEditType
  children: ChildrenType
  onDelete: () => void
}

const RuleTriggerWrapper = ({ trigger, children, onDelete }: RuleTriggerWrapperProps) => {
  const { t } = useLocoTranslation()

  const blogId =
    trigger.type === AutomationRuleTriggersEnum.blog_page_form_subscribed
      ? trigger.blog.id
      : undefined

  const blogEntity =
    trigger.type === AutomationRuleTriggersEnum.blog_page_form_subscribed
      ? trigger.page.type
      : undefined

  const formSubscribeFunnelId =
    trigger.type === AutomationRuleTriggersEnum.form_subscribed
      ? trigger.funnelStep.funnel.id
      : undefined

  const newSaleFunnelId =
    trigger.type === AutomationRuleTriggersEnum.new_sale ? trigger.funnelStep.funnel.id : undefined

  const cancelSaleFunnelId =
    trigger.type === AutomationRuleTriggersEnum.sale_canceled
      ? trigger.funnelStep.funnel.id
      : undefined

  const pageVisitedFunnelId =
    trigger.type === AutomationRuleTriggersEnum.page_visited
      ? trigger.funnelStep.funnel.id
      : undefined

  const webinarFunnelId =
    trigger.type === AutomationRuleTriggersEnum.registered_to_webinar
      ? trigger.webinar.registrationFunnelStep.funnel.id
      : undefined

  const triggerCourseId =
    trigger.type === AutomationRuleTriggersEnum.module_completed ||
    trigger.type === AutomationRuleTriggersEnum.lecture_completed
      ? trigger.course.id
      : undefined

  const triggerModuleId =
    trigger.type === AutomationRuleTriggersEnum.lecture_completed ? trigger.module?.id : undefined

  const subscriptionFailedFunnelId =
    trigger.type === AutomationRuleTriggersEnum.subscription_payment_failed
      ? trigger.funnelStep.funnel.id
      : undefined

  const triggerMailingId =
    trigger.type === AutomationRuleTriggersEnum.email_link_clicked ? trigger.mailing.id : undefined

  const triggerDigitalStoreId =
    trigger.type === AutomationRuleTriggersEnum.digital_store_form_subscribed
      ? trigger.digitalStore.id
      : undefined

  const triggerWebsiteId =
    trigger.type === AutomationRuleTriggersEnum.website_form_subscribed
      ? trigger.website.id
      : undefined

  const { triggersData, setShouldFetch } = useTriggersData({
    trigger: trigger.type,
    blogId,
    formSubscribeFunnelId,
    newSaleFunnelId,
    cancelSaleFunnelId,
    pageVisitedFunnelId,
    blogEntity,
    webinarFunnelId,
    triggerCourseId,
    triggerModuleId,
    triggerMailingId,
    subscriptionFailedFunnelId,
    triggerDigitalStoreId,
    triggerWebsiteId,
  })

  const renderTriggerLabel = ({
    label,
    additionalLabel,
  }: {
    label: string
    additionalLabel?: JSX.Element
  }) => (
    <div className={'flex gap-1.5'}>
      <div className="flex gap-0.5 text-sm font-medium">
        <span>{label}</span>
        <span className="text-danger">*</span>
      </div>
      {additionalLabel}
    </div>
  )

  return (
    <div className={'flex flex-col gap-3'}>
      <div className={'flex justify-between gap-3'}>
        <div className={'flex gap-3'}>
          <div className={'[&>svg]:h-[40x] [&>svg]:w-[40px] w-[40px]'}>
            {getTriggerIcon(trigger.type)}
          </div>
          <div className={'flex flex-col gap-1'}>
            <span className={'text-text-primary text-[15px] font-bold'}>
              {t(triggerActionLocoKeys[trigger.type])}
            </span>
            <span>{t(triggerDescriptionLocoKeys[trigger.type])}</span>
          </div>
        </div>
        <button onClick={onDelete} className={'group h-fit outline-hidden'}>
          <CloseIcon
            className={
              'main-transition-colors fill-fill-primary group-hover:fill-text-primary group-focus-visible:fill-text-primary'
            }
          />
        </button>
      </div>
      {children({
        data: triggersData,
        onOpen: (slug: AutomationRuleTriggersDataEnum) =>
          setShouldFetch(prev => ({ ...prev, [slug]: true })),
        onClear: (slug: AutomationRuleTriggersDataEnum) =>
          setShouldFetch(prev => ({ ...prev, [slug]: false })),
        renderTriggerLabel,
      })}
    </div>
  )
}

export default RuleTriggerWrapper
